import { ReactNode } from "react";

interface HeroSectionProps {
  title?: string | ReactNode;
  subtitle?: string | ReactNode;
  children?: ReactNode;
  className?: string;
  showStatistics?: boolean;
  showTrustIndicators?: boolean;
  backgroundImage?: string;
  variant?: "default" | "compact";
}

export const HeroSection = ({
  title,
  subtitle,
  children,
  className = "",
  showStatistics = false,
  showTrustIndicators = false,
  backgroundImage = "/lovable-uploads/2c916b40-43c9-4dc9-9c1d-3b61266c291c.png",
  variant = "default"
}: HeroSectionProps) => {
  const isCompact = variant === "compact";
  
  return (
    <section 
      className={`relative overflow-hidden bg-hero-dark ${
        isCompact ? "min-h-[60vh]" : "min-h-screen"
      } ${className}`} 
      style={{ marginTop: '-80px', paddingTop: '80px' }}
    >
      {/* Background image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('${backgroundImage}')`,
          zIndex: 1
        }}
      />
      
      <div className={`container mx-auto px-4 relative z-10 ${
        isCompact ? "py-16 lg:py-20" : "py-16 lg:py-24"
      }`}>
        <div className="text-center">
          {/* Title and Subtitle */}
          {(title || subtitle) && (
            <div className={`space-y-6 relative z-20 ${
              isCompact ? "mb-8" : "mb-16"
            }`}>
              {title && (
                <h1 className={`font-bold text-white leading-tight max-w-5xl mx-auto ${
                  isCompact ? "text-3xl lg:text-5xl" : "text-4xl lg:text-6xl"
                }`}>
                  {title}
                </h1>
              )}
              
              {subtitle && (
                <p className={`text-gray-300 leading-relaxed max-w-4xl mx-auto ${
                  isCompact ? "text-base lg:text-lg" : "text-lg lg:text-xl"
                }`}>
                  {subtitle}
                </p>
              )}
            </div>
          )}
          
          {/* Custom content */}
          {children && (
            <div className="relative z-20 mb-12">
              {children}
            </div>
          )}
          
          {/* Trust Indicators */}
          {showTrustIndicators && (
            <div className="flex items-center gap-6 justify-center mb-20">
              <div className="flex -space-x-2">
                <img src="https://images.unsplash.com/photo-1649972904349-6e44c42644a7?w=40&h=40&fit=crop&crop=face" alt="Profile" className="w-10 h-10 rounded-full border-2 border-white shadow-minimal object-cover grayscale" />
                <img src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=40&h=40&fit=crop&crop=face" alt="Profile" className="w-10 h-10 rounded-full border-2 border-white shadow-minimal object-cover grayscale" />
                <img src="https://images.unsplash.com/photo-1581092795360-fd1ca04f0952?w=40&h=40&fit=crop&crop=face" alt="Profile" className="w-10 h-10 rounded-full border-2 border-white shadow-minimal object-cover grayscale" />
              </div>
              <div className="text-sm text-white">
                <div className="font-semibold">2.1k+ Audio-Kreationen</div>
                <div className="text-gray-300">in den letzten 30 Tagen</div>
              </div>
            </div>
          )}
        </div>
        
        {/* Statistics */}
        {showStatistics && !isCompact && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-20">
            <div className="text-center">
              <div className="text-4xl lg:text-5xl font-bold text-white mb-2">7</div>
              <div className="text-white font-medium">Minuten durchschnittlich</div>
              <div className="text-sm text-gray-300">Von Idee zu Veröffentlichung</div>
            </div>
            <div className="text-center">
              <div className="text-4xl lg:text-5xl font-bold text-white mb-2">2.1K</div>
              <div className="text-white font-medium">Veröffentlichte Hörbücher</div>
              <div className="text-sm text-gray-300">In den letzten 30 Tagen</div>
            </div>
            <div className="text-center">
              <div className="text-4xl lg:text-5xl font-bold text-white mb-2">95%</div>
              <div className="text-white font-medium">Erfolgsrate</div>
              <div className="text-sm text-gray-300">Bestseller in 48h</div>
            </div>
          </div>
        )}
        
        {/* Trusted by section */}
        {showStatistics && !isCompact && (
          <div className="text-center mt-16">
            <p className="text-sm text-gray-300 mb-6">AI Publishing on:</p>
            <div className="flex flex-col gap-4">
              <div className="flex justify-center items-center flex-wrap gap-6">
                <div className="w-40 h-16 border-2 border-white rounded-lg flex items-center justify-center transition-all duration-300 hover:bg-white/20 hover:scale-105 cursor-pointer backdrop-blur-sm">
                  <span className="text-xl font-semibold text-white">Audible</span>
                </div>
                <div className="w-40 h-16 border-2 border-white rounded-lg flex items-center justify-center transition-all duration-300 hover:bg-white/20 hover:scale-105 cursor-pointer backdrop-blur-sm">
                  <span className="text-xl font-semibold text-white">Google Play</span>
                </div>
                <div className="w-40 h-16 border-2 border-white rounded-lg flex items-center justify-center transition-all duration-300 hover:bg-white/20 hover:scale-105 cursor-pointer backdrop-blur-sm">
                  <span className="text-xl font-semibold text-white">Apple</span>
                </div>
                <div className="w-40 h-16 border-2 border-white rounded-lg flex items-center justify-center transition-all duration-300 hover:bg-white/20 hover:scale-105 cursor-pointer backdrop-blur-sm">
                  <span className="text-xl font-semibold text-white">Spotify</span>
                </div>
              </div>
              <div className="flex justify-center items-center flex-wrap gap-6">
                <div className="w-40 h-16 border-2 border-white rounded-lg flex items-center justify-center transition-all duration-300 hover:bg-white/20 hover:scale-105 cursor-pointer backdrop-blur-sm">
                  <span className="text-xl font-semibold text-white">Kobo</span>
                </div>
                <div className="w-40 h-16 border-2 border-white rounded-lg flex items-center justify-center transition-all duration-300 hover:bg-white/20 hover:scale-105 cursor-pointer backdrop-blur-sm">
                  <span className="text-xl font-semibold text-white">Tolino</span>
                </div>
                <div className="w-40 h-16 border-2 border-white rounded-lg flex items-center justify-center transition-all duration-300 hover:bg-white/20 hover:scale-105 cursor-pointer backdrop-blur-sm">
                  <span className="text-xl font-semibold text-white">und mehr ...</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};